import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import Tabs from "../../component/Tabs";
import ListItem from "../../component/ListItem";
import SelectItem from "../../component/MenuCard/SelectItem";
import { addNote, clearCartForTable, fetchMenuData } from "../../redux/action";
import { createTakeawayOrder, getTakeawaySettings } from "./takeawayApi";
import { ButtonLoader } from "../../component/common/Loader";
import "./takeaway.css";

const CART_KEY = "TAKEAWAY_ORDER";

const TakeawayCreate = () => {
  const dispatch = useDispatch();
  const menu = useSelector((state) => state?.user?.menu);
  const cart = useSelector((state) => state?.user?.cart?.[CART_KEY] || {});
  const [categoryId, setCategoryId] = useState("");
  const [search, setSearch] = useState("");
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [created, setCreated] = useState(null);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [form, setForm] = useState({
    customerName: "", customerPhone: "", orderSource: 4,
    fulfillmentType: 0,
    paymentMode: "", isPaid: false, discountAmount: "", applyServiceCharge: false,
    pickupTime: "", specialInstructions: ""
  });

  useEffect(() => {
    dispatch(fetchMenuData());
    getTakeawaySettings().then(setSettings).catch((error) => toast.error(error.message));
  }, [dispatch]);

  useEffect(() => {
    if (!categoryId && menu?.categories?.length) setCategoryId(menu.categories[0].categoryId);
  }, [categoryId, menu]);

  useEffect(() => {
    if (!isCartOpen) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setIsCartOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [isCartOpen]);

  const entries = useMemo(() => Object.entries(cart), [cart]);
  const itemCount = entries.reduce((sum, [, item]) => sum + Number(item.selected || 0), 0);
  const subtotal = entries.reduce((sum, [, item]) => sum + Number(item.price || 0) * Number(item.selected || 0), 0);
  const isDineIn = form.fulfillmentType === 1;
  const packagingCharge = isDineIn ? 0 : Number(settings?.packagingCharge || 0);
  const discountAmount = form.isPaid ? Number(form.discountAmount || 0) : 0;
  const displayedTotal = Math.max(0, subtotal + packagingCharge - discountAmount);
  const activeCategory = menu?.categories?.find((category) => category.categoryId === categoryId);
  const filteredCategory = activeCategory ? {
    ...activeCategory,
    items: (activeCategory.items || []).filter((item) =>
      (item.foodName || item.name || "").toLowerCase().includes(search.trim().toLowerCase()))
  } : null;
  const tabs = (menu?.categories || []).map((category) => ({
    categoryId: category.categoryId,
    category: category.categoryName,
    isSelected: category.categoryId === categoryId
  }));

  const update = (name, value) => setForm((current) => ({ ...current, [name]: value }));
  const updatePaymentReceived = (value) => setForm((current) => ({
    ...current,
    isPaid: value === "yes",
    paymentMode: value === "yes" ? current.paymentMode : "",
    discountAmount: value === "yes" ? current.discountAmount : "",
    applyServiceCharge: value === "yes" ? current.applyServiceCharge : false
  }));

  const submit = async (event) => {
    event.preventDefault();
    if (!entries.length) return toast.warning("Add at least one menu item.");
    if (form.isPaid && !["Cash", "Card", "UPI"].includes(form.paymentMode)) {
      return toast.warning("Choose Cash, Card or UPI for a received payment.");
    }
    setLoading(true);
    try {
      const result = await createTakeawayOrder({
        fulfillmentType: form.fulfillmentType,
        orderSource: Number(form.orderSource),
        customerName: form.customerName || null,
        customerPhone: form.customerPhone || null,
        paymentMode: form.isPaid ? form.paymentMode : (settings?.allowPayAtPickup ? "PayAtPickup" : "Cash"),
        paymentStatus: form.isPaid ? 1 : 0,
        discountAmount: form.isPaid ? Number(form.discountAmount || 0) : 0,
        applyServiceCharge: form.isPaid && form.applyServiceCharge,
        pickupTime: isDineIn ? null : (form.pickupTime || null),
        specialInstructions: form.specialInstructions || null,
        items: entries.map(([, item]) => ({
          menuId: item.menuId,
          sizeOptionId: item.selectedSize?.sizeOptionId || null,
          quantity: Number(item.selected),
          note: item.note || null,
          addonItemIds: (item.selectedAddons || []).map((addon) => addon.addonItemId)
        }))
      });
      setCreated(result);
      dispatch(clearCartForTable(CART_KEY));
      toast.success(`Takeaway ${result.tokenNumber} created.`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (created) return (
    <div className="takeaway-page">
      <div className="takeaway-success">
        <span>Pickup token</span><strong>{created.tokenNumber}</strong>
        <p>Order total: ₹{Number(created.grandTotal).toFixed(2)}</p>
        <div className="takeaway-actions">
          <button onClick={() => { setCreated(null); setForm((current) => ({ ...current, customerName: "", customerPhone: "", paymentMode: "", isPaid: false, discountAmount: "", applyServiceCharge: false, pickupTime: "", specialInstructions: "" })); }}>Create another</button>
          <Link to="/takeaway/orders">Open live board</Link>
        </div>
      </div>
    </div>
  );

  if (settings && (!settings.isEnabled || !settings.isAcceptingOrders)) return (
    <div className="takeaway-page">
      <div className="takeaway-empty">
        <h2>Takeaway ordering is paused</h2>
        <p>An Admin must enable Takeaway and “Accepting orders” in settings.</p>
        <Link to="/takeaway-setting">Open Takeaway settings</Link>
      </div>
    </div>
  );

  return (
    <div className="takeaway-page">
      <div className="takeaway-title"><div><h1>New Takeaway Order</h1><p>Counter, phone or staff order</p></div><Link to="/takeaway/orders">Live orders</Link></div>
      <div className="takeaway-create-grid">
        <section className="takeaway-menu-panel">
          <input className="takeaway-search" placeholder="Search menu item" value={search} onChange={(e) => setSearch(e.target.value)} />
          <Tabs tabs={tabs} tabSelect={setCategoryId} />
          <ListItem category={filteredCategory} ActionComponent={SelectItem} tableName={CART_KEY} respectSchedule />
        </section>

        <div
          className={`takeaway-cart-backdrop${isCartOpen ? " is-open" : ""}`}
          aria-hidden="true"
          onClick={() => setIsCartOpen(false)}
        />
        <form className={`takeaway-cart-panel${isCartOpen ? " is-open" : ""}`} onSubmit={submit}>
          <div className="takeaway-cart-heading">
            <div><h2>Order details</h2><span>{itemCount} {itemCount === 1 ? "item" : "items"} selected</span></div>
            <button type="button" className="takeaway-cart-close" aria-label="Close order details" onClick={() => setIsCartOpen(false)}>×</button>
          </div>
          <div className="takeaway-field-row">
            <label>Customer name (optional)<input maxLength="120" value={form.customerName} onChange={(e) => update("customerName", e.target.value)} /></label>
            <label>Phone (optional)<input type="tel" maxLength="25" value={form.customerPhone} onChange={(e) => update("customerPhone", e.target.value)} /></label>
          </div>
          <div className="takeaway-fulfillment-toggle" role="group" aria-label="Order type">
            <button type="button" aria-pressed={isDineIn} className={isDineIn ? "active" : ""} onClick={() => setForm((current) => ({ ...current, fulfillmentType: 1, pickupTime: "" }))}>Dine-in</button>
            <button type="button" aria-pressed={!isDineIn} className={!isDineIn ? "active" : ""} onClick={() => update("fulfillmentType", 0)}>Pick-up</button>
          </div>
          <label>Order source<select value={form.orderSource} onChange={(e) => update("orderSource", e.target.value)}><option value="4">Staff</option><option value="2">Counter</option><option value="3">Phone</option></select></label>
          {!isDineIn && settings?.allowScheduledPickup && <label>Pickup time<input type="datetime-local" value={form.pickupTime} onChange={(e) => update("pickupTime", e.target.value)} /></label>}

          <div className="takeaway-cart-lines">
            {!entries.length && <p className="takeaway-muted">Select items from the menu.</p>}
            {entries.map(([key, item]) => <div className="takeaway-cart-line" key={key}>
              <div><strong>{item.name}</strong>{item.selectedSize?.sizeName && <small>{item.selectedSize.sizeName}</small>}{item.selectedAddons?.length > 0 && <small>{item.selectedAddons.map((addon) => addon.itemName).join(", ")}</small>}</div>
              <span>{item.selected} × ₹{Number(item.price).toFixed(2)}</span>
              <input aria-label={`Note for ${item.name}`} placeholder="Item note" maxLength="1000" value={item.note || ""} onChange={(e) => dispatch(addNote({ tableName: CART_KEY, id: key, note: e.target.value }))} />
            </div>)}
          </div>

          <label>Order instructions<textarea maxLength="1000" rows="2" value={form.specialInstructions} onChange={(e) => update("specialInstructions", e.target.value)} /></label>
          <label>Payment received<select value={form.isPaid ? "yes" : "no"} onChange={(e) => updatePaymentReceived(e.target.value)}><option value="no">No</option><option value="yes">Yes</option></select></label>
          {form.isPaid && <div className="takeaway-payment-details">
            <label>Payment mode<select value={form.paymentMode} onChange={(e) => update("paymentMode", e.target.value)} required><option value="">Select payment mode</option><option>Cash</option><option>Card</option><option>UPI</option></select></label>
            <label>Discount amount<input type="number" min="0" step="0.01" value={form.discountAmount} onChange={(e) => update("discountAmount", e.target.value)} /></label>
            <label>Apply service charge<select value={form.applyServiceCharge ? "yes" : "no"} onChange={(e) => update("applyServiceCharge", e.target.value === "yes")}><option value="no">No</option><option value="yes">Yes</option></select></label>
          </div>}
          <div className="takeaway-total"><span>Order type</span><strong>{isDineIn ? "Dine-in" : "Pick-up"}</strong><span>Subtotal</span><strong>₹{subtotal.toFixed(2)}</strong><span>Packaging</span><strong>₹{packagingCharge.toFixed(2)}</strong>{discountAmount > 0 && <><span>Discount</span><strong>-₹{discountAmount.toFixed(2)}</strong></>}<span>Total before configured charges</span><strong>₹{displayedTotal.toFixed(2)}</strong></div>
          {form.isPaid && form.applyServiceCharge && <small className="takeaway-muted">Configured service charge and taxes are added when the bill is created.</small>}
          <button className="takeaway-primary" disabled={loading || !entries.length} aria-busy={loading}>{loading ? <ButtonLoader label="Creating..." /> : "Create order & token"}</button>
        </form>
      </div>
      <div className="takeaway-mobile-cart-bar">
        <div><strong>{itemCount} {itemCount === 1 ? "item" : "items"}</strong><span>₹{displayedTotal.toFixed(2)}</span></div>
        <button type="button" onClick={() => setIsCartOpen(true)}>View order</button>
      </div>
    </div>
  );
};

export default TakeawayCreate;
